// frontend/src/pages/NameGeneratorPage.js
//
// Renders the name/username generator family from data/name-generators.json.
//
// MEASURED 2026-08-18. Five pages covering 29,200/mo, every one at KD <= 8 with a
// wall in the low single digits. The entry point is `instagram name generator`
// (3,600/mo, KD 0, wall 1.5) where the top four are thesocialcat (2 referring
// domains), Hootsuite (0), Buffer (1) and Ahrefs (217) — the median is what
// matters and it is 1.5, so this is reachable with a page that simply exists.
//
// THREE OF THESE PAGES ARE MERGES, NOT SINGLE KEYWORDS. SERP-checked 2026-08-18:
//   instagram name + instagram username generator -> ONE page. thesocialcat,
//     Buffer, Birdeye and codedesign rank the SAME URL on both. 8,000/mo.
//   youtube name + youtube channel name generator -> ONE page. Hootsuite,
//     Renderforest, Squarespace and Postiz, same URLs in nearly the same order.
//   aesthetic + cute username generator -> ONE page. cuteinternet, codeitbro and
//     glyphy rank across both, plus the same Pinterest hub.
// Building these as six or eight pages would have been self-cannibalisation of
// exactly the kind standing rule 7 exists to prevent.
//
// `tiktok name generator` is deliberately NOT its own page: its SERP is half FONT
// generators (tiktokfont.com, lingojam, nickfinder) — the same trap as `tweet
// generator`. Hootsuite ranks #1 on BOTH TikTok terms with its
// /tiktok-username-generator URL, so one page on the clean term takes both.
//
// The head term `name generator` (201,000/mo) and `username generator` (90,500,
// wall 83) are NOT targeted. Sideways entry on the platform variants, the same
// approach that worked for youtube-banner-size.
//
// useLocation, NOT useParams — static routes, no :param.
import '../custom.css'

import React from 'react'
import { Container, Row, Col, Button, Accordion, Image } from 'react-bootstrap'
import { useLocation } from 'react-router-dom'
import { CheckCircleFill } from 'react-bootstrap-icons'

import Seo, { SITE_URL } from '../components/Seo'
import NameGenerator from '../components/NameGenerator'
import pages from '../data/name-generators.json'
import NotFound from './NotFound'

const HERO = {
  'instagram-name-generator':   ['/assets/images/articles/name-instagram.webp', 'Someone choosing a handle on a phone beside a laptop'],
  'tiktok-username-generator':  ['/assets/images/articles/name-tiktok.webp', 'A creator setting up a phone on a tripod to film'],
  'youtube-name-generator':     ['/assets/images/articles/name-youtube.webp', 'A video creator adjusting a camera on a tripod'],
  'aesthetic-username-generator': ['/assets/images/articles/name-aesthetic.webp', 'A soft flatlay of roses, pencils and a notepad'],
  'podcast-name-generator':     ['/assets/images/articles/name-podcast.webp', 'A podcast microphone and headphones on a desk'],
}

export default function NameGeneratorPage () {
  const { pathname } = useLocation()
  const slug = pathname.replace(/^\/+tools\/+/, '').replace(/\/+$/, '')
  const p = pages.find(x => x.slug === slug)
  if (!p) return <NotFound />

  const url = `/tools/${p.slug}`
  const [heroSrc, heroAlt] = HERO[p.slug] || HERO['instagram-name-generator']

  return (
    <>
      <Seo
        title={p.seoTitle}
        description={p.seoDescription}
        path={url}
        image={heroSrc}
        jsonLd={[
          {
            '@context': 'https://schema.org', '@type': 'FAQPage',
            mainEntity: p.faqs.map(([q, a]) => ({
              '@type': 'Question', name: q,
              acceptedAnswer: { '@type': 'Answer', text: a },
            })),
          },
          {
            '@context': 'https://schema.org', '@type': 'WebApplication',
            name: p.h1,
            url: `${SITE_URL}${url}`,
            applicationCategory: 'UtilitiesApplication',
            operatingSystem: 'Any',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          },
        ]}
      />

      {/* ───────── HERO ───────── */}
      <section className='full-bleed py-5 bg-primary-soft'>
        <Container fluid='lg'>
          <Row className='align-items-center g-4'>
            <Col lg={7}>
              <h1 className='display-5 fw-bold mb-3'>{p.h1}</h1>
              <p className='lead mb-0'>{p.sub}</p>
            </Col>
            <Col lg={5}>
              <Image src={heroSrc} alt={heroAlt} fluid rounded loading='eager'
                     width={1600} height={1066} className='shadow-sm' />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── THE ANSWER FIRST ─────────
          Standing rule 3: no sign-up link above the answer. There is none until
          the "What is Greatly Social" section, well below the tool. */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          {/* eslint-disable-next-line react/no-danger */}
          <p className='fs-5' dangerouslySetInnerHTML={{ __html: p.intro }} />
          <p className='text-muted fs-5 mb-0'>{p.intro2}</p>
        </Container>
      </section>

      {/* ───────── THE TOOL ───────── */}
      <section className='pb-5'>
        <Container fluid='lg' style={{ maxWidth: 940 }}>
          <NameGenerator cfg={p} />
        </Container>
      </section>

      {/* ───────── THE RULES TABLE ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 900 }}>
          <h2 className='fw-bold mb-4'>{p.rules_title}</h2>
          <div className='table-responsive'>
            <table className='table table-bordered align-middle bg-white mb-0'>
              <tbody>
                {p.rules.map(([label, value, note]) => (
                  <tr key={label}>
                    <td className='fw-semibold' style={{ width: '28%' }}>{label}</td>
                    <td className='text-primary fw-semibold' style={{ width: '26%' }}>{value}</td>
                    <td className='text-muted'>{note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Container>
      </section>

      {/* ───────── DEPTH ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          {p.sections.map(([heading, body]) => (
            <div className='mb-4' key={heading}>
              <h2 className='fw-bold h4 mb-2'>{heading}</h2>
              <p className='text-muted mb-0'>{body}</p>
            </div>
          ))}
        </Container>
      </section>

      {/* ───────── WHAT IS GREATLY SOCIAL (standing rule 4) ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 940 }}>
          <Row className='align-items-center g-4'>
            <Col md={6}>
              <h2 className='fw-bold mb-3'>What is Greatly Social?</h2>
              <p className='text-muted'>
                Greatly Social is a social media scheduler and content calendar. At Greatly
                Social you can describe your business once, let AI draft the month — captions
                and images — and schedule it to all eight networks from a single calendar.
              </p>
              <p className='text-muted'>
                Naming the account is the first ten minutes. Keeping it posting is the part
                that decides whether it works. Plans start at $9/month and the trial needs
                no card.
              </p>
              <Button variant='primary' href='/register' className='me-2'>
                Start using Greatly Social free
              </Button>
              <Button variant='outline-primary' href='/plans'>See plans</Button>
            </Col>
            <Col md={6}>
              {/* A real capture of the product, not stock — standing rule 2. */}
              <Image src='/assets/logo/aipost.webp' alt='AI drafting a post in Greatly Social'
                     fluid rounded className='shadow-sm' loading='lazy' />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── CROSS-LINKS (standing rule 8) ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 880 }}>
          <h2 className='fw-bold h4 mb-3'>More free tools</h2>
          <Row className='g-3'>
            {/* Sibling generators first — without these each new page in the family
                depends solely on the footer for inbound links. */}
            {pages.filter(x => x.slug !== p.slug).map(x => (
              <Col md={4} key={x.slug}>
                <a href={`/tools/${x.slug}`} className='d-block p-3 rounded-3 text-decoration-none h-100'
                   style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                  <div className='fw-semibold'>{x.h1}</div>
                  <div className='text-muted small'>Free, no sign-up</div>
                </a>
              </Col>
            ))}
            {[['/tools/instagram-bio-generator', 'Bio generator', 'Fits the character limit'],
              ['/tools/instagram-caption-generator', 'Caption generator', 'Every network at once'],
              ['/tools/instagram-post-size', 'Instagram post size', 'Dimensions and a resizer'],
              ['/tools/engagement-rate-calculator', 'Engagement rate calculator', 'Per-platform formulas'],
              ['/best-time-to-post', 'Best time to post', 'A schedule for your audience'],
              ['/tools', 'All free tools', 'The full set']].map(([href, title, note]) => (
              <Col md={4} key={href}>
                <a href={href} className='d-block p-3 rounded-3 text-decoration-none h-100'
                   style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                  <div className='fw-semibold'>{title}</div>
                  <div className='text-muted small'>{note}</div>
                </a>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ───────── PRODUCT ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 880 }}>
          <h2 className='fw-bold mb-3'>The account is easy to name and hard to keep</h2>
          <p className='text-muted fs-5'>
            Most accounts stall in week three, when posting stops being new and starts being
            a task. Greatly Social exists for week three.
          </p>
          <Row className='g-3 mb-4'>
            {[['Describe it once, get the month', 'AI drafts the posts; you edit rather than write.'],
              ['Eight networks, one post', 'A separate caption per network on the same post.'],
              ['A real content calendar', 'Drag to reschedule. Empty days are obvious.'],
              ['Link in bio included', 'A hosted page that updates with your latest posts.']].map(([t, b]) => (
              <Col md={6} key={t}>
                <div className='d-flex align-items-start gap-2'>
                  <CheckCircleFill className='text-success mt-1 flex-shrink-0' />
                  <div><div className='fw-semibold'>{t}</div><div className='text-muted small'>{b}</div></div>
                </div>
              </Col>
            ))}
          </Row>
          <Button variant='primary' size='lg' href='/register' className='me-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-primary' size='lg' href='/plans'>See plans from $9/month</Button>
        </Container>
      </section>

      {/* ───────── FAQ (standing rule 5) ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <h2 className='fw-bold mb-4 text-center'>Common questions</h2>
          <Accordion flush defaultActiveKey='0'>
            {p.faqs.map(([q, a], i) => (
              <Accordion.Item eventKey={String(i)} key={i}>
                <Accordion.Header>{q}</Accordion.Header>
                <Accordion.Body className='text-muted'>{a}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>

      {/* ───────── CTA (standing rule 3) ───────── */}
      <section className='full-bleed py-5 text-center bg-primary text-white'>
        <div className='mx-auto px-4' style={{ maxWidth: 720 }}>
          <h2 className='fw-bold mb-3'>{p.cta_title}</h2>
          <p className='lead mb-4'>{p.cta_body}</p>
          <Button variant='light' size='lg' href='/register' className='me-2 mb-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-light' size='lg' href='/calendar-view' className='mb-2'>
            See the calendar
          </Button>
        </div>
      </section>
    </>
  )
}
